import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TournamentService } from '../../services/tournament.service';
import { Tournament } from '../../models/tournament';
import _ from 'lodash';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '../../services/log.service';
import { AuxiliarService } from '../../services/auxiliar.service';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { Option } from '../../models/option';
import { dateAfterValidator } from '../../utils/validators/dateAfterValidator';
import { defaultIfEmpty, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-tournament-new',
  imports: [
    ButtonModule,
    DatePickerModule,
    FormsModule,
    InputNumberModule,
    ReactiveFormsModule,
    SelectModule,
    ToggleButtonModule,
    TranslateModule,
  ],
  templateUrl: './tournament-new.component.html',
  styleUrl: './tournament-new.component.scss'
})
export class TournamentNewComponent implements OnInit {
  public _ = _;
  private readonly destroy$ = inject(DestroyRef);
  public divisionOptions: Option[] = [];
  public formTournament!: FormGroup;
  public maxTeamsOptions: Option[] = [];
  public tournament: Tournament | undefined;

  get fT(): { [index: string]: any; } {
    return this.formTournament ? this.formTournament.controls : {};
  }

  constructor(
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private logService: LogService,
    private route: ActivatedRoute,
    private router: Router,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ){}

  async ngOnInit() {
    // get tournament (if edit)
    const tournamentId = this.route.snapshot.paramMap.get('id');
    if(tournamentId) this.tournament = await this.getTournament(tournamentId);
    if(tournamentId && !this.tournament) this.router.navigate(['admin', 'tournaments']);
    // get selects options
    this.getOptions();
    this.translate.onLangChange.subscribe(() => {
      this.getOptions();
    });
    // load form
    this.loadForm();
  }

  private async getTournament(tournamentId: string) {
    try {
      const res: any = await firstValueFrom(
        this.tournamentService.getTournament(tournamentId).pipe(
          defaultIfEmpty(null),
        )
      );
      this.logService.info('[Tournament] - getTournament', res);
      return res;
    } catch (err: any) {
      this.logService.error('[Tournament] - getTournaments', err, this.translate.instant('error.tournament'));
      return undefined;
    }
  }

  private getOptions(): void {
    // division options
    this.divisionOptions = this.auxiliarService.getTournamentDivisionOptions();
    // max teams options
    this.maxTeamsOptions = this.auxiliarService.getTournamentMaxTeamsOptions();
  }

  private loadForm(): void {
    this.formTournament = this.formBuilder.group({
      division: [_.find(this.divisionOptions, {value: this.tournament?.division}), [Validators.required]],
      endDate: [this.tournament?.endDate ? moment(this.tournament?.endDate).toDate() : '', []],
      hasConsolation: [this.tournament?.hasConsolation ?? true, []],
      id: [this.tournament?.id, []],
      maxTeams: [this.tournament?.maxTeams ? 
        _.find(this.maxTeamsOptions, {value: this.tournament?.maxTeams}) : null, [Validators.required]],
      name: [this.tournament?.name, []],
      province: [this.tournament?.province, [Validators.required]],
      startDate: [this.tournament?.startDate ? moment(this.tournament?.startDate).toDate() : '', [Validators.required]],
      town: [this.tournament?.town, [Validators.required]],
    });

    this.fT['endDate'].setValidators([dateAfterValidator(this.fT['startDate'])]);

    this.cdr.markForCheck();
  }

  public save(): void {
    this.formTournament.markAllAsTouched();
    if(!this.formTournament.valid) return;
    let saveData: Tournament = _.cloneDeep(this.formTournament.getRawValue());
    _.set(saveData, 'division', _.get(saveData, 'division.value', saveData.division));
    _.set(saveData, 'maxTeams', _.get(saveData, 'maxTeams.value', saveData.maxTeams));
    if(!saveData.endDate) delete saveData.endDate;
    if(this.tournament) {
      this.tournamentService.updateTournament(saveData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Tournament] - updateTournament', res, this.translate.instant('success.tournament-update'));
          setTimeout(() => {
            this.router.navigate(['admin', 'tournaments']);
          });
        },
        error: (err) => {
          this.logService.error('[Tournament] - updateTournament', err, this.translate.instant('error.tournament-update'));
        }
      });
    } else {
      this.tournamentService.createTournament(saveData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Tournament] - createTournament', res, this.translate.instant('success.tournament-create'));
          setTimeout(() => {
            this.router.navigate(['admin', 'tournaments']);
          });
        },
        error: (err) => {
          this.logService.error('[Tournament] - createTournament', err, this.translate.instant('error.tournament-create'));
        }
      });
    }
  }

  public cancel(): void {
    this.router.navigate(['admin', 'tournaments']);
  }
}
